import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { organizationService } from '@/services/api/organization.service';
import { sanitizeErrorMessage } from '@/lib/error-utils';
import type { User } from '@/types/auth';

/**
 * Hook to fetch team members list
 */
export function useTeamMembers() {
  return useQuery({
    queryKey: ['team-members'],
    queryFn: () => organizationService.getUsers()
  });
}

/**
 * Hook to update a user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('team');

  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<User> }) =>
      organizationService.updateUser(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({
        title: t('toasts.updateSuccessTitle'),
        description: t('toasts.updateSuccessDescription')
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.updateErrorTitle'),
        description: sanitizeErrorMessage(error, t('toasts.updateErrorDescription')),
        variant: 'destructive'
      });
    }
  });
}

/**
 * Hook to toggle user active status
 */
export function useToggleUserActive() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('team');

  return useMutation({
    mutationFn: (id: number) => organizationService.toggleUserActive(id),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({
        title: t(user.active ? 'toasts.activatedTitle' : 'toasts.deactivatedTitle'),
        description: t('toasts.statusChangedDescription', {
          name: `${user.firstName} ${user.lastName}`,
          state: user.active ? t('toasts.stateActivated') : t('toasts.stateDeactivated')
        })
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.statusErrorTitle'),
        description: sanitizeErrorMessage(error, t('toasts.statusErrorDescription')),
        variant: 'destructive'
      });
    }
  });
}

/**
 * Hook to change user role
 */
export function useChangeUserRole() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('team');

  return useMutation({
    mutationFn: ({ id, role }: { id: number; role: User['role'] }) =>
      organizationService.changeUserRole(id, role),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({
        title: t('toasts.roleUpdatedTitle'),
        description: t('toasts.roleChangedDescription', {
          name: `${user.firstName} ${user.lastName}`,
          role: user.role === 'admin' ? t('toasts.roleAdmin') : t('toasts.roleStaff')
        })
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.roleErrorTitle'),
        description: sanitizeErrorMessage(error, t('toasts.roleErrorDescription')),
        variant: 'destructive'
      });
    }
  });
}

/**
 * Hook to delete a user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('team');

  return useMutation({
    mutationFn: (id: number) => organizationService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', 'limits'] });
      toast({
        title: t('toasts.removedTitle'),
        description: t('toasts.removedDescription')
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.removeErrorTitle'),
        description: sanitizeErrorMessage(error, t('toasts.removeErrorDescription')),
        variant: 'destructive'
      });
    }
  });
}
